import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CustomDropdown from "../../../CustomControls/dropdown";
import dropdownOptions from "./TrackerConfig.json";
import { useSelector } from "react-redux";
import { Endpoints } from "../../../Global/Endpoints";
import { useTranslation } from 'react-i18next';

Modal.setAppElement("#root");

const TrackerModal = ({ isOpen, onClose, trackerType, isEditMode = false }) => {
  const { t, i18n } = useTranslation();
  const [lastPeriodDate, setLastPeriodDate] = useState(null);
  const [periodLength, setPeriodLength] = useState(null);
  const [cycleLength, setCycleLength] = useState(null);
  const [consent, setConsent] = useState(true);

  const email = useSelector((state) => state.email.email);
  const token = localStorage.getItem("AccessToken");
  const userData = useSelector((state) => state.userData.userData);
  const ssoData = useSelector((state) => state.sso.ssoUserData);
  const isHighmarkPortal = ssoData?.Portal === "Highmark";
  const devicetype = isHighmarkPortal ? "HighmarkPortal" : "PatientPortal";

  const contentLabel =
    trackerType ==="menopause" ? "Menopause Tracker" : "Fertility Tracker";
  const consentText =
    trackerType === "menopause"
      ? t('menopausetype')
      : t('fertilitytype');

  const headers = {
    "Content-Type": "application/x-www-form-urlencoded",
    Authorization: `Bearer ${token}`,
  };

  const daysOptions = dropdownOptions.daysOptions.map((day) => ({
    id: day.id,
    name: day.name,
  }));

  const periodLengthOptions = dropdownOptions.periodLengthOptions.map(
    (length) => ({
      id: length.id,
      name: length.name,
    })
  );

  const validateForm = () => {
    return lastPeriodDate && periodLength && cycleLength;
  };

  const loadMenstrualCycleData = async () => {
    const dataParams = new URLSearchParams();
    dataParams.append("_Params", JSON.stringify({ EmailID: email,DeviceType:devicetype }));
    try {
      const response = await axios.post(
        Endpoints.LoadMenstrualCycleData,
        dataParams,
        { headers }
      );
      if (response.data) {
        const {
          CycleStartDate,
          LastPeriodDays,
          CycleDays,
          MenstruationDataConsent,
        } = response.data;

        setLastPeriodDate(
          CycleStartDate
            ? new Date(
                new Date(CycleStartDate).toISOString().split("T")[0] +
                  "T00:00:00"
              )
            : null
        );

        setPeriodLength(
          daysOptions.find((option) => option.name === LastPeriodDays)
        );
        setCycleLength(
          periodLengthOptions.find((option) => option.name === CycleDays)
        );
        setConsent(MenstruationDataConsent === "Y");
      }
    } catch (error) {
      console.error(error);
      toast.error(t('toasterror'));
    }
  };

  const resetFields = () => {
    if (!lastPeriodDate) setLastPeriodDate(null);
    setPeriodLength(null);
    setCycleLength(null);
    setConsent(true);
  };

  useEffect(() => {
    if (isOpen) {
      if (isEditMode) {
        loadMenstrualCycleData();
      } else {
        resetFields();
      }
    }
  }, [isOpen, isEditMode]);

  const saveMenstrualCycleData = async () => {
    const payload = {
      CycleStartDate: lastPeriodDate?.toLocaleDateString("en-US"),
      LastPeriodDays: periodLength?.name,
      CycleDays: cycleLength?.name,
      EmailID: email,
      DeviceType:devicetype,
      Status: consent ? "Y" : "N",
    };

    const cycleData = `_Params=${encodeURIComponent(JSON.stringify(payload))}`;

    try {
      const response = await axios.post(
        Endpoints.SaveMenstrualCycle,
        cycleData,
        { headers }
      );

      if (response.data.Result === 1) {
        toast.success(t('toastsuccess'));
      } else {
        toast.error(t('toastsave'));
      }
    } catch (error) {
      console.error(error);
      toast.error(t('failedtosavemenstrual'));
    }
  };

  const updateConsentStatus = async () => {
    const consentData = new URLSearchParams();
    consentData.append(
      "_Params",
      JSON.stringify({
        EmailID: email,
        Status: consent ? "Y" : "N",
        Contract: userData.ContractIdentified,
        DeviceType:devicetype
      })
    );

    try {
      const response = await axios.post(
        Endpoints.UpdateMenstrualDataConsent,
        consentData,
        { headers }
      );
      if (response.data.Result === 1) {
        // toast.success("Consent status updated successfully.");
      } else {
        toast.error(t('failedtoupdateconsent'));
      }
    } catch (error) {
      console.error(error);
      toast.error(t('failedtoupdateconsent'));
    }
  };

  const handleSave = async () => {
    if (validateForm()) {
      await saveMenstrualCycleData();
      await updateConsentStatus();
      onClose();
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onRequestClose={onClose}
        contentLabel={contentLabel}
        className="tracker-modal-content-x"
        overlayClassName="tracker-modal-overlay"
        shouldCloseOnOverlayClick={false}
      >
        <div>
          <h2>{contentLabel}</h2>
          <div className="model-form-wrapper">
            <div className="mb-4 row align-items-center">
              <label className="col-sm-4 model-form-label">
                {t('lastperiod')}
              </label>
              <div className="col-sm-6">
                <DatePicker
                  selected={lastPeriodDate}
                  onChange={setLastPeriodDate}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="MM/DD/YYYY"
                  maxDate={new Date()}
                  className="custom-datepicker form-control win-form-control-c calendar-control mobile-view react-datepicker-ignore-onclickoutside"
                  onKeyDown={(e) => e.preventDefault()}
                />
              </div>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="col-sm-4 model-form-label">
                {t('howlonglastperiod')}
              </label>
              <div className="col-sm-6 custom-dropdown-forced-380px-in-tab custom-dropdown-100pc-only-mobile">
                <CustomDropdown
                  options={daysOptions.map((option) => option.name)} // Pass names as options
                  selectedOption={periodLength?.name || ""} // Extract the name from the selected option
                  onChange={(selectedName) =>
                    setPeriodLength(daysOptions.find((option) => option.name === selectedName)) // Set the corresponding object
                  }
                  placeholder="Days"
                />
              </div>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="col-sm-4 model-form-label">
               {t('averagecycle')}
              </label>
              <div className="col-sm-6 custom-dropdown-forced-380px-in-tab custom-dropdown-100pc-only-mobile">
                <CustomDropdown
                  options={periodLengthOptions.map((option) => option.name)}
                  selectedOption={cycleLength?.name || ""}
                  onChange={(selectedName) =>
                    setCycleLength(periodLengthOptions.find((option) => option.name === selectedName)) // Set the corresponding object
                  }
                  // onChange={(selected) => setCycleLength(selected)}
                  searchable={true}
                  placeholder="Days"
                />
              </div>
            </div>
            <div className="mb-4 row align-items-center">
              <label className="col-sm-4 model-form-label"></label>
              <div className="col-sm-6 d-flex">
                <div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      className="switch-input"
                      checked={consent}
                      onChange={() => setConsent(!consent)}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>
                <span className="switch-label">{consentText}</span>
              </div>
            </div>
            <div className="mb-4 row align-items-center justify-content-end">
              <label className="col-sm-4 model-form-label"></label>
              <div className="col-sm-8">
                <button
                  onClick={() => {
                    resetFields();
                    onClose();
                  }}
                  className="win-btn-secondary model-popup-btn me-2 mb-3 mb-sm-0"
                >
                  {t('cancelbutton')}
                </button>
                <button
                  onClick={handleSave}
                  disabled={!validateForm()}
                  className="win-btn-primary model-popup-btn"
                >
                 {t('Save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default TrackerModal;